import { useState } from 'react';
import {
  Shield,
  Lock,
  Key,
  AlertTriangle,
  CheckCircle,
  Eye,
  EyeOff,
  RefreshCw,
  Smartphone,
  Globe,
  Clock,
  LogOut,
  User,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { useAdminAuth, changeAdminPassword } from '@/services/adminAuth';

interface LoginAttempt {
  id: string;
  ip: string;
  location: string;
  device: string;
  status: 'success' | 'failed';
  timestamp: Date;
}

interface ActiveSession {
  id: string;
  device: string;
  browser: string;
  ip: string;
  location: string;
  lastActive: Date;
  current: boolean;
}

// Demo data
const loginAttempts: LoginAttempt[] = [
  { id: '1', ip: '192.168.1.100', location: 'Istanbul, TR', device: 'Chrome / Windows', status: 'success', timestamp: new Date() },
  { id: '2', ip: '192.168.1.100', location: 'Istanbul, TR', device: 'Chrome / Windows', status: 'success', timestamp: new Date(Date.now() - 3600000) },
  { id: '3', ip: '45.67.89.123', location: 'Unknown', device: 'Firefox / Linux', status: 'failed', timestamp: new Date(Date.now() - 7200000) },
  { id: '4', ip: '192.168.1.100', location: 'Istanbul, TR', device: 'Safari / macOS', status: 'success', timestamp: new Date(Date.now() - 86400000) },
];

const activeSessions: ActiveSession[] = [
  { id: '1', device: 'MacBook Pro', browser: 'Chrome 120', ip: '192.168.1.100', location: 'Istanbul, TR', lastActive: new Date(), current: true },
  { id: '2', device: 'iPhone 15', browser: 'Safari', ip: '192.168.1.101', location: 'Istanbul, TR', lastActive: new Date(Date.now() - 1800000), current: false },
];

export function AdminSecurity() {
  const { session, logout } = useAdminAuth();
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [passwords, setPasswords] = useState({ current: '', new: '', confirm: '' });
  const [showPasswords, setShowPasswords] = useState({ current: false, new: false, confirm: false });
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);

  const handlePasswordChange = async () => {
    setPasswordError(null);
    setPasswordSuccess(false);

    if (passwords.new !== passwords.confirm) {
      setPasswordError('Yeni sifreler eslesmez');
      return;
    }

    if (passwords.new.length < 8) {
      setPasswordError('Sifre en az 8 karakter olmali');
      return;
    }

    const result = await changeAdminPassword(session?.user.email || '', passwords.current, passwords.new);

    if (result.success) {
      setPasswordSuccess(true);
      setPasswords({ current: '', new: '', confirm: '' });
      setShowPasswordForm(false);
    } else {
      setPasswordError(result.error || 'Sifre degistirilemedi');
    }
  };

  const formatTime = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);

    if (minutes < 1) return 'Az once';
    if (minutes < 60) return `${minutes} dakika once`;
    if (minutes < 1440) return `${Math.floor(minutes / 60)} saat once`;
    return date.toLocaleDateString('tr-TR');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-text-primary">Guvenlik</h1>
        <p className="text-text-secondary">Hesap guvenlik ayarlarini ve oturumlari yonetin</p>
      </div>

      {/* Security Overview */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-bg-card border border-border-default rounded-xl p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 rounded-lg bg-green-500/10">
              <Shield className="text-green-500" size={20} />
            </div>
            <div>
              <p className="font-medium text-text-primary">Hesap Durumu</p>
              <p className="text-sm text-green-500">Guvenli</p>
            </div>
          </div>
        </div>
        <div className="bg-bg-card border border-border-default rounded-xl p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 rounded-lg bg-accent/10">
              <Smartphone className={cn('text-accent', twoFactorEnabled ? '' : 'opacity-50')} size={20} />
            </div>
            <div>
              <p className="font-medium text-text-primary">2FA</p>
              <p className={cn('text-sm', twoFactorEnabled ? 'text-green-500' : 'text-yellow-500')}>
                {twoFactorEnabled ? 'Aktif' : 'Pasif'}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-bg-card border border-border-default rounded-xl p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 rounded-lg bg-blue-500/10">
              <Clock className="text-blue-500" size={20} />
            </div>
            <div>
              <p className="font-medium text-text-primary">Son Giris</p>
              <p className="text-sm text-text-secondary">
                {session?.user.lastLogin ? new Date(session.user.lastLogin).toLocaleString('tr-TR') : 'N/A'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Password Change */}
      <div className="bg-bg-card border border-border-default rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Key size={20} className="text-accent" />
            <div>
              <h2 className="font-semibold text-text-primary">Sifre Degistir</h2>
              <p className="text-sm text-text-secondary">Hesap sifrenizi guncelleyin</p>
            </div>
          </div>
          {!showPasswordForm && (
            <button
              onClick={() => setShowPasswordForm(true)}
              className="px-4 py-2 rounded-lg bg-accent text-white hover:bg-accent/90"
            >
              Sifre Degistir
            </button>
          )}
        </div>

        {showPasswordForm && (
          <div className="space-y-4 max-w-md">
            {passwordError && (
              <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-500 text-sm">
                {passwordError}
              </div>
            )}
            {passwordSuccess && (
              <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/20 text-green-500 text-sm">
                Sifreniz basariyla degistirildi!
              </div>
            )}
            <div className="relative">
              <input
                type={showPasswords.current ? 'text' : 'password'}
                placeholder="Mevcut Sifre"
                value={passwords.current}
                onChange={(e) => setPasswords({ ...passwords, current: e.target.value })}
                className="w-full px-4 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none pr-10"
              />
              <button
                onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-text-tertiary"
              >
                {showPasswords.current ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            <div className="relative">
              <input
                type={showPasswords.new ? 'text' : 'password'}
                placeholder="Yeni Sifre"
                value={passwords.new}
                onChange={(e) => setPasswords({ ...passwords, new: e.target.value })}
                className="w-full px-4 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none pr-10"
              />
              <button
                onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-text-tertiary"
              >
                {showPasswords.new ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            <div className="relative">
              <input
                type={showPasswords.confirm ? 'text' : 'password'}
                placeholder="Yeni Sifre (Tekrar)"
                value={passwords.confirm}
                onChange={(e) => setPasswords({ ...passwords, confirm: e.target.value })}
                className="w-full px-4 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none pr-10"
              />
              <button
                onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-text-tertiary"
              >
                {showPasswords.confirm ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => { setShowPasswordForm(false); setPasswords({ current: '', new: '', confirm: '' }); }}
                className="px-4 py-2 rounded-lg border border-border-default text-text-secondary hover:text-text-primary"
              >
                Iptal
              </button>
              <button
                onClick={handlePasswordChange}
                className="px-4 py-2 rounded-lg bg-accent text-white hover:bg-accent/90"
              >
                Kaydet
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Two-Factor Auth */}
      <div className="bg-bg-card border border-border-default rounded-xl p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Smartphone size={20} className="text-accent" />
            <div>
              <h2 className="font-semibold text-text-primary">Iki Faktorlu Dogrulama</h2>
              <p className="text-sm text-text-secondary">Hesabinizi ekstra guvenlik katmaniyla koruyun</p>
            </div>
          </div>
          <button
            onClick={() => setTwoFactorEnabled(!twoFactorEnabled)}
            className={cn(
              'relative w-12 h-6 rounded-full transition-colors',
              twoFactorEnabled ? 'bg-accent' : 'bg-bg-elevated'
            )}
          >
            <span className={cn(
              'absolute top-1 w-4 h-4 rounded-full bg-white transition-all',
              twoFactorEnabled ? 'left-7' : 'left-1'
            )} />
          </button>
        </div>
      </div>

      {/* Active Sessions */}
      <div className="bg-bg-card border border-border-default rounded-xl overflow-hidden">
        <div className="p-4 border-b border-border-default">
          <h2 className="font-semibold text-text-primary">Aktif Oturumlar</h2>
        </div>
        <div className="divide-y divide-border-default">
          {activeSessions.map(s => (
            <div key={s.id} className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={cn('w-2 h-2 rounded-full', s.current ? 'bg-green-500' : 'bg-gray-400')} />
                <div>
                  <p className="text-sm font-medium text-text-primary">{s.device} - {s.browser}</p>
                  <p className="text-xs text-text-tertiary">{s.location} • {s.ip}</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-xs text-text-tertiary">{formatTime(s.lastActive)}</span>
                {s.current ? (
                  <span className="text-xs text-green-500">Bu cihaz</span>
                ) : (
                  <button className="text-red-500 hover:text-red-600">
                    <LogOut size={16} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Login History */}
      <div className="bg-bg-card border border-border-default rounded-xl overflow-hidden">
        <div className="p-4 border-b border-border-default">
          <h2 className="font-semibold text-text-primary">Giris Gecmisi</h2>
        </div>
        <div className="divide-y divide-border-default">
          {loginAttempts.map(attempt => (
            <div key={attempt.id} className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                {attempt.status === 'success' ? (
                  <CheckCircle size={16} className="text-green-500" />
                ) : (
                  <AlertTriangle size={16} className="text-red-500" />
                )}
                <div>
                  <p className="text-sm text-text-primary">{attempt.device}</p>
                  <p className="text-xs text-text-tertiary">{attempt.location} • {attempt.ip}</p>
                </div>
              </div>
              <div className="text-right">
                <p className={cn('text-xs', attempt.status === 'success' ? 'text-green-500' : 'text-red-500')}>
                  {attempt.status === 'success' ? 'Basarili' : 'Basarisiz'}
                </p>
                <p className="text-xs text-text-tertiary">{formatTime(attempt.timestamp)}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
